import { useCallback, useEffect, useRef, useState } from "react";

export interface UseTtsReturn {
  speak: (text: string) => void;
  stop: () => void;
  isSpeaking: boolean;
  voiceEnabled: boolean;
  toggleVoice: () => void;
  setVoiceEnabled: (enabled: boolean) => void;
}

const stripHtml = (html: string): string => {
  const container = document.createElement("div");
  container.innerHTML = html;
  return container.textContent ?? "";
};

export function useTTS(lang = "es-MX"): UseTtsReturn {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const voicesRef = useRef<SpeechSynthesisVoice[]>([]);

  const isSupported = typeof window !== "undefined" && "speechSynthesis" in window;

  useEffect(() => {
    if (!isSupported) return;

    const syncVoices = () => {
      voicesRef.current = window.speechSynthesis.getVoices();
    };

    syncVoices();
    window.speechSynthesis.addEventListener("voiceschanged", syncVoices);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", syncVoices);
  }, [isSupported]);

  const stop = useCallback(() => {
    if (!isSupported) return;
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, [isSupported]);

  const speak = useCallback(
    (text: string) => {
      if (!voiceEnabled || !isSupported) return;

      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(stripHtml(text));
      utterance.lang = lang;
      utterance.rate = 1.05;
      utterance.pitch = 1;
      utterance.volume = 1;

      const prefix = lang.split("-")[0];
      const voices =
        voicesRef.current.length > 0 ? voicesRef.current : window.speechSynthesis.getVoices();
      const matchingVoice =
        voices.find((voice) => voice.lang.startsWith(lang)) ??
        voices.find((voice) => voice.lang.startsWith(prefix));
      matchingVoice && (utterance.voice = matchingVoice);

      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);

      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    },
    [isSupported, lang, voiceEnabled],
  );

  const toggleVoice = useCallback(() => {
    setVoiceEnabled((previous) => {
      const next = !previous;
      if (!next && isSupported) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
      }
      return next;
    });
  }, [isSupported]);

  return { speak, stop, isSpeaking, voiceEnabled, toggleVoice, setVoiceEnabled };
}

export default useTTS;
